import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

export default function Login() {
  const { login, user, role, loading, forcePasswordChange } = useAuth()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  // Set when sign-in fails specifically because the address is unconfirmed —
  // a server-side account state, not a wrong password. Rendered as its own
  // panel with a next step rather than as a generic red error.
  const [unconfirmed, setUnconfirmed] = useState(false)

  // Redirect if already logged in — respect force_password_change so a
  // freshly-invited user whose magic-link landed them here (rather than
  // /set-password) still ends up at the right screen. Client route is
  // /account (not /dashboard, which doesn't exist and would 404 through
  // the catch-all).
  useEffect(() => {
    if (loading || !user) return
    if (forcePasswordChange) {
      navigate('/set-password', { replace: true })
    } else if (role === 'admin') {
      navigate('/admin', { replace: true })
    } else {
      navigate('/account', { replace: true })
    }
  }, [user, role, loading, forcePasswordChange, navigate])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setUnconfirmed(false)

    if (!email.trim()) { setError('Please enter your email address.'); return }
    if (!password)      { setError('Please enter your password.');       return }

    setSubmitting(true)
    try {
      await login(email, password)
      // Navigation handled by useEffect above once auth state updates
    } catch (err) {
      const msg = err?.message || 'Login failed. Please check your credentials and try again.'
      // "Email not confirmed" is not a credentials problem — the password is
      // fine, the account just has email_confirmed_at set to null server-side.
      // Nothing the user can do on this screen fixes it, so say so and give
      // them a way out instead of a bare error they'll retype their password
      // against. `code` only exists on newer gotrue-js errors, so match both.
      if (err?.code === 'email_not_confirmed' || msg.includes('Email not confirmed')) {
        setError(null)
        setUnconfirmed(true)
      } else {
        setError(
          msg.includes('Invalid login credentials')
            ? 'Invalid email or password. Please try again.'
            : msg
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 size={28} className="text-blue-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-slate-800 px-8 py-8 text-center">
            <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center mx-auto mb-4 shadow-lg p-2">
              <img src="/logo.png" alt="OPS 3PL" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight">OPS 3PL PTY LTD</h1>
            <p className="text-slate-400 text-sm mt-1">Client Portal</p>
            <p className="text-slate-500 text-xs mt-1">ABN: 64 676 295 891</p>
          </div>

          {/* Form */}
          <div className="px-8 py-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-1">Sign in to your account</h2>
            <p className="text-sm text-slate-500 mb-6">Enter your credentials to continue</p>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-5 text-sm text-red-700">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-red-500" />
                <p>{error}</p>
              </div>
            )}

            {unconfirmed && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-5 text-sm text-amber-800">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-amber-500" />
                <div className="space-y-2">
                  <p className="font-medium">This account needs re-confirming.</p>
                  <p>
                    Your password is fine — the address just has to be confirmed on
                    our side before you can sign in. If you have a recent invite
                    email from OPS 3PL, opening the link in it will do it.
                    Otherwise get in touch and we'll unlock it.
                  </p>
                  <a
                    href={
                      'mailto:steven@opsconsulting.com.au?subject=' +
                      encodeURIComponent('Portal sign-in: email not confirmed') +
                      '&body=' +
                      encodeURIComponent(
                        `I can't sign in to the OPS 3PL client portal.\n\n` +
                        `Account: ${email}\nError: Email not confirmed\n`
                      )
                    }
                    className="inline-block font-medium underline hover:no-underline"
                  >
                    Email us to unlock this account
                  </a>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div>
                <label htmlFor="email" className="label">Email address</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input"
                  disabled={submitting}
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="label">Password</label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="input pr-10"
                    disabled={submitting}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full justify-center py-2.5 mt-2"
              >
                {submitting
                  ? <><Loader2 size={16} className="animate-spin" /> Signing in…</>
                  : 'Sign in'
                }
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-xs mt-6">
          &copy; {new Date().getFullYear()} OPS 3PL PTY LTD &middot; ABN 64 676 295 891
        </p>
      </div>
    </div>
  )
}
